// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import type { SubmitFormEvent } from 'components/Mantine/Modals/WrappedModal';

/**
 * Wraps the confirm action of a `WrappedModal` / `WrappedPopover` with a `submitting` state:
 * awaits `formSubmit` (when confirming via a form submission) or `onConfirm`, then closes
 * per `closeOnConfirm`. A thrown error skips the close, leaving the surface open.
 */
export const useOnConfirmWrapper = ({
  onConfirm,
  formSubmit,
  closeOnConfirm,
  onClose,
}: {
  onConfirm?: () => Promise<void> | void;
  formSubmit?: (e: SubmitFormEvent) => Promise<void> | void;
  closeOnConfirm: boolean;
  onClose?: () => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const onConfirmWrapper = useCallback(
    async (event?: SubmitFormEvent) => {
      setSubmitting(true);
      try {
        if (formSubmit && event) await formSubmit(event);
        else await onConfirm?.();
      } finally {
        setSubmitting(false);
      }
      if (closeOnConfirm) onClose?.();
    },
    [closeOnConfirm, formSubmit, onClose, onConfirm],
  );

  return { submitting, onConfirmWrapper };
};
